/**
 * SidePanelLayout component.
 *
 * Side panel expansion layout. Renders the queue list on the left and an
 * expandable detail panel on the right. This is the default expansion mode.
 */

import type { CSSProperties } from 'react'
import * as React from 'react'
import { X, Maximize2, Minimize2 } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/shared/components/ui'
import { SidePanelProvider } from './SidePanelContext'

export interface SidePanelLayoutProps {
  /** Whether the panel is expanded. */
  expanded: boolean
  /** Called when the panel should be closed. */
  onClose: () => void
  /** Content to render in the side panel. */
  panel: React.ReactNode
  /** Title displayed in the panel header. Defaults to "Details". */
  panelTitle?: string
  /** Main list content. */
  children: React.ReactNode
  /**
   * Whether the panel is currently displayed in maximized (modal) mode.
   * When `true` the side panel is hidden and the panel content is
   * rendered inside a centred dialog with a much larger viewport
   * (max ~1100px wide), giving operators more room to read long
   * execution summaries and proposed resolutions side-by-side with
   * the email body / tool arguments.
   */
  maximized?: boolean
  /**
   * Called when the maximize/minimize toggle is clicked. When omitted,
   * the toggle button is hidden — preserving the legacy single-mode
   * layout for callers that haven't opted in.
   */
  onToggleMaximize?: () => void
  /**
   * Extra action nodes rendered in the panel header, before the
   * maximize/minimize and close buttons. Used to lift item-level
   * actions (e.g. "View agent session" icon, the more-actions
   * dropdown for an approval) out of the inner panel content and onto
   * the top header row so they sit alongside the maximize/close
   * controls.
   */
  panelHeaderExtras?: React.ReactNode
}

/**
 * Side panel expansion layout.
 *
 * Renders the queue list on the left and an expandable detail
 * panel on the right. This is the default expansion mode.
 */
export function SidePanelLayout({
  expanded,
  onClose,
  panel,
  panelTitle = 'Details',
  children,
  maximized = false,
  onToggleMaximize,
  panelHeaderExtras,
}: SidePanelLayoutProps) {
  // When maximized, the side panel is replaced by a modal dialog showing
  // the same `panel` content but with much more room to breathe. We still
  // shrink the main list area so re-opening the sidebar (by minimizing
  // the modal) doesn't cause a jarring re-layout.
  const sidebarVisible = expanded && !maximized

  return (
    <TooltipProvider>
      <div style={styles.container}>
        {/* Main list area */}
        <div style={expanded ? { ...styles.mainArea, ...styles.mainAreaExpanded } : styles.mainArea}>{children}</div>

        {/* Side panel - slides in from right on lg screens */}
        {sidebarVisible && (
          <aside data-testid="side-panel-expansion" style={styles.aside} className="side-panel-slide">
            {/* Panel header */}
            <div style={styles.panelHeader}>
              <h2 style={styles.panelTitle}>{panelTitle}</h2>
              <div style={styles.panelHeaderActions}>
                {panelHeaderExtras}
                {onToggleMaximize && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={onToggleMaximize}
                        aria-label="Maximize panel"
                        data-testid="side-panel-maximize-button"
                      >
                        <Maximize2 style={styles.closeIcon} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Maximize</TooltipContent>
                  </Tooltip>
                )}
                <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close panel">
                  <X style={styles.closeIcon} />
                </Button>
              </div>
            </div>

            {/* Panel content */}
            <div style={styles.panelContent}>
              <SidePanelProvider value={sidebarPanelContext}>{panel}</SidePanelProvider>
            </div>
          </aside>
        )}

        {/* Backdrop for mobile */}
        {sidebarVisible && <div style={styles.backdrop} onClick={onClose} aria-hidden="true" className="side-panel-backdrop" />}

        {/* Maximized modal — shown when `maximized` is true. Closing the
            modal returns to sidebar mode rather than fully closing the
            panel, matching the operator's mental model of "expand /
            contract" rather than "open / dismiss". */}
        <Dialog
          open={expanded && maximized}
          onOpenChange={(open) => {
            if (!open) onToggleMaximize?.()
          }}
        >
          <DialogContent
            style={styles.maximizedDialogContent}
            data-testid="side-panel-maximized-dialog"
            hideCloseButton
          >
            {/* Hidden accessibility labels — Radix Dialog requires a
                DialogTitle / DialogDescription be present for screen
                readers, but we render our own visible header bar
                below. The visually-hidden style follows the standard
                "sr-only" pattern. */}
            <DialogTitle style={srOnlyStyle}>{panelTitle}</DialogTitle>
            <DialogDescription style={srOnlyStyle}>
              Expanded view of the {panelTitle.toLowerCase()} panel.
            </DialogDescription>
            <div style={styles.maximizedHeader}>
              <h2 style={styles.panelTitle}>{panelTitle}</h2>
              <div style={styles.panelHeaderActions}>
                {panelHeaderExtras}
                {onToggleMaximize && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={onToggleMaximize}
                        aria-label="Minimize panel"
                        data-testid="side-panel-minimize-button"
                      >
                        <Minimize2 style={styles.closeIcon} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Minimize</TooltipContent>
                  </Tooltip>
                )}
                <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close panel">
                  <X style={styles.closeIcon} />
                </Button>
              </div>
            </div>
            <div style={styles.maximizedBody}>
              <SidePanelProvider value={maximizedPanelContext}>{panel}</SidePanelProvider>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  )
}

/**
 * Inline expansion layout.
 *
 * Alternative to `SidePanelLayout` - renders a single stacked column
 * where the expanded approval card appears directly under whatever
 * row owns it.
 */
export interface InlineExpansionLayoutProps<T extends { id: string }> {
  /** All items to render. */
  items: readonly T[]
  /** ID of the expanded item, if any. */
  expandedId: string | null
  /** Render the collapsed row for an item. */
  renderItem: (item: T) => React.ReactNode
  /** Render the expanded panel for an item (detail view). */
  renderPanel: (item: T) => React.ReactNode
  /** Called when the panel should close (e.g. Esc or close button). */
  onClose: () => void
  /** Fallback content when `items` is empty. */
  empty?: React.ReactNode
}

export function InlineExpansionLayout<T extends { id: string }>({
  items,
  expandedId,
  renderItem,
  renderPanel,
  onClose,
  empty,
}: InlineExpansionLayoutProps<T>) {
  if (items.length === 0 && empty) {
    return <>{empty}</>
  }

  return (
    <div style={inlineStyles.list}>
      {items.map((item) => {
        const isExpanded = item.id === expandedId
        return (
          <React.Fragment key={item.id}>
            {renderItem(item)}
            {isExpanded ? (
              <div style={inlineStyles.panel} data-testid="inline-expansion-panel">
                <div style={inlineStyles.panelHeader}>
                  <h2 style={styles.panelTitle}>Details</h2>
                  <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close inline panel">
                    <X style={styles.closeIcon} />
                  </Button>
                </div>
                <div style={inlineStyles.panelContent}>{renderPanel(item)}</div>
              </div>
            ) : null}
          </React.Fragment>
        )
      })}
    </div>
  )
}

/**
 * Stable context values for the side panel display mode. Defined at
 * module scope so context consumers don't re-render every time
 * SidePanelLayout itself re-renders.
 */
const sidebarPanelContext = { maximized: false }
const maximizedPanelContext = { maximized: true }

/**
 * Standard "sr-only" visually-hidden style. Keeps content available to
 * screen readers without taking up visible layout space — used for the
 * accessibility-required DialogTitle/Description on the maximized
 * dialog (we render our own visible header bar separately).
 */
const srOnlyStyle: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    gap: '16px',
    minWidth: 0,
  },
  mainArea: {
    flex: 1,
    // A flex item defaults to `min-width: auto`, which means this column
    // refuses to shrink below its widest descendant. Queue rows carry long
    // single-line titles and summaries, so without this the main area grows
    // past the viewport and the operator has to scroll horizontally to reach
    // each row's right-hand controls. Clamping here lets the rows ellipsize.
    minWidth: 0,
    // Only animate width/flex-basis — transitioning `all` here causes
    // every nested style change (hover backgrounds, focus rings) to
    // animate at 200ms, producing a perceptibly laggy list area.
    transition: 'width 200ms cubic-bezier(0.4, 0, 0.2, 1), flex-basis 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  mainAreaExpanded: {
    width: '50%',
    flex: 'none',
  },
  aside: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 40,
    width: '100%',
    maxWidth: '576px',
    background: 'var(--bg-secondary)',
    borderLeft: '1px solid var(--border-subtle)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)',
    flexShrink: 0,
    zIndex: 2,
  },
  panelHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  panelTitle: {
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  maximizedDialogContent: {
    width: '92vw',
    maxWidth: '1280px',
    height: '92vh',
    maxHeight: '92vh',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  maximizedHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)',
    flexShrink: 0,
  },
  // NOTE: padding-top is intentionally 0 here. The approval card's
  // sticky action bar lives inside this scroll container with
  // `top: 0`, so any padding-top above the article would scroll up
  // with the content and leave a visible gap of text peeking above
  // the sticky bar before it reaches the scrollport edge. Visual
  // breathing room above the header is provided by the header itself.
  maximizedBody: {
    padding: '0 28px 32px',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    wordBreak: 'break-word' as const,
  },
  closeIcon: {
    width: '16px',
    height: '16px',
  },
  // Same reasoning as `maximizedBody.padding` — top padding stripped
  // so the sticky action bar inside the approval card can reach the
  // scrollport edge without a visible content-bleed gutter above it.
  panelContent: {
    padding: '0 16px 16px',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    wordBreak: 'break-word' as const,
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 30,
    background: 'rgba(0, 0, 0, 0.5)',
  },
}

const inlineStyles: Record<string, CSSProperties> = {
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  panel: {
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-subtle)',
  },
  panelContent: {
    padding: '16px',
  },
}

export default SidePanelLayout
